import type { SupplyTableResponse } from '../services/supplyService';

const STORAGE_PREFIX = 'stockerai_supply_table_v3';
/** v6: MP/MOQ es overlay cliente; una sola foto merged/actionable por warehouse+periodo. */
// 7: stock_actual y ventas del almacén seleccionado (backend supply_table_sorted_v3).
const CACHE_VERSION = 7 as const;

/** Alineado con `SUPPLY_TABLE_CACHE_TZ` / `SUPPLY_TABLE_CACHE_CUTOFFS_LOCAL` en backend. */
const SUPPLY_TABLE_CACHE_TZ = 'Europe/Madrid';
const SUPPLY_TABLE_CACHE_CUTOFFS_LOCAL: ReadonlyArray<readonly [number, number]> = [
  [7, 10],
  [13, 0],
];

export interface SupplyTableCacheBlob {
  version: typeof CACHE_VERSION;
  /** Ultimo corte Europe/Madrid superado (p. ej. ``2026-06-10T1300``); invalidacion 2x/dia. */
  cachePeriod: string;
  /** Respuesta completa (suggested_greater_zero=false en API); filtros "Mostra" en cliente. */
  merged?: {
    response: SupplyTableResponse;
    /** @deprecated v6+: ignorado al leer; MP/MOQ se aplica en cliente. */
    applyMoqMp?: boolean;
  };
  /**
   * Primera pintura (suggested_greater_zero=true). No sustituye `merged`:
   * "Mostrar todos" y F5 con catálogo completo siguen exigiendo `merged`.
   */
  actionable?: {
    response: SupplyTableResponse;
    /** @deprecated v6+: ignorado al leer; MP/MOQ se aplica en cliente. */
    applyMoqMp?: boolean;
  };
  /** Legacy; ya no se escribe desde SupplyPage. */
  splitKpi?: {
    suggestedGreaterZero?: boolean;
    response: SupplyTableResponse;
  };
  /** Respuestas por hash de filtros de tabla API. */
  splitTables: Record<string, SupplyTableResponse>;
}

const memoryFallback = new Map<number, SupplyTableCacheBlob>();

/** Prefetch (Home) se registra aqui para abortar escrituras stale tras mutacion. */
type SupplyTableCacheInvalidationListener = (warehouseId?: number) => void;
const cacheInvalidationListeners: SupplyTableCacheInvalidationListener[] = [];

export function onSupplyTableCacheInvalidated(
  listener: SupplyTableCacheInvalidationListener,
): void {
  cacheInvalidationListeners.push(listener);
}

function notifySupplyTableCacheInvalidated(warehouseId?: number): void {
  cacheInvalidationListeners.forEach((listener) => listener(warehouseId));
}

function storageKey(warehouseId: number): string {
  return `${STORAGE_PREFIX}:w:${warehouseId}`;
}

type WallParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
};

function getTimeZoneParts(date: Date, timeZone: string): WallParts {
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  const map: Record<string, string> = {};
  for (const part of fmt.formatToParts(date)) {
    if (part.type !== 'literal') {
      map[part.type] = part.value;
    }
  }
  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    hour: Number(map.hour),
    minute: Number(map.minute),
  };
}

/** Convierte reloj de pared Europe/Madrid → epoch UTC (ms). */
function madridWallToUtcMs(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
): number {
  let guess = Date.UTC(year, month - 1, day, hour, minute, 0, 0);
  for (let i = 0; i < 3; i += 1) {
    const parts = getTimeZoneParts(new Date(guess), SUPPLY_TABLE_CACHE_TZ);
    const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, 0, 0);
    const desired = Date.UTC(year, month - 1, day, hour, minute, 0, 0);
    guess += desired - asUtc;
  }
  return guess;
}

function addCalendarDays(
  year: number,
  month: number,
  day: number,
  delta: number,
): { year: number; month: number; day: number } {
  const utc = new Date(Date.UTC(year, month - 1, day + delta));
  return {
    year: utc.getUTCFullYear(),
    month: utc.getUTCMonth() + 1,
    day: utc.getUTCDate(),
  };
}

function formatPeriodLabel(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
): string {
  const mo = String(month).padStart(2, '0');
  const da = String(day).padStart(2, '0');
  const ho = String(hour).padStart(2, '0');
  const mi = String(minute).padStart(2, '0');
  return `${year}-${mo}-${da}T${ho}${mi}`;
}

/** Ultimo corte Europe/Madrid superado; alinea invalidacion frontend con backend (07:10 y 13:00). */
export function getSupplyTableCachePeriodString(date = new Date()): string {
  const nowMs = date.getTime();
  const nowParts = getTimeZoneParts(date, SUPPLY_TABLE_CACHE_TZ);
  const candidates: Array<{ ms: number; label: string }> = [];

  for (const dayOffset of [-1, 0]) {
    const cal = addCalendarDays(nowParts.year, nowParts.month, nowParts.day, dayOffset);
    for (const [hour, minute] of SUPPLY_TABLE_CACHE_CUTOFFS_LOCAL) {
      const ms = madridWallToUtcMs(cal.year, cal.month, cal.day, hour, minute);
      if (ms <= nowMs) {
        candidates.push({
          ms,
          label: formatPeriodLabel(cal.year, cal.month, cal.day, hour, minute),
        });
      }
    }
  }

  if (candidates.length === 0) {
    const yesterday = addCalendarDays(nowParts.year, nowParts.month, nowParts.day, -1);
    const [h, min] = SUPPLY_TABLE_CACHE_CUTOFFS_LOCAL[SUPPLY_TABLE_CACHE_CUTOFFS_LOCAL.length - 1];
    return formatPeriodLabel(yesterday.year, yesterday.month, yesterday.day, h, min);
  }

  candidates.sort((a, b) => b.ms - a.ms);
  return candidates[0].label;
}

/** @deprecated Usar `getSupplyTableCachePeriodString`. Mantenido por compatibilidad de imports. */
export function getLocalCalendarDayString(date = new Date()): string {
  return getSupplyTableCachePeriodString(date).slice(0, 10);
}

function emptyBlob(period: string): SupplyTableCacheBlob {
  return {
    version: CACHE_VERSION,
    cachePeriod: period,
    splitTables: {}
  };
}

function normalizeBlob(raw: unknown, warehouseId: number, currentPeriod: string): SupplyTableCacheBlob | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (o.version !== CACHE_VERSION) return null;
  const period = typeof o.cachePeriod === 'string' ? o.cachePeriod : null;
  if (!period || period !== currentPeriod) {
    memoryFallback.delete(warehouseId);
    try {
      localStorage.removeItem(storageKey(warehouseId));
    } catch {
      /* ignore */
    }
    return null;
  }
  return {
    version: CACHE_VERSION,
    cachePeriod: period,
    merged: o.merged as SupplyTableCacheBlob['merged'],
    actionable: o.actionable as SupplyTableCacheBlob['actionable'],
    splitKpi: o.splitKpi as SupplyTableCacheBlob['splitKpi'],
    splitTables: (o.splitTables && typeof o.splitTables === 'object'
      ? o.splitTables
      : {}) as Record<string, SupplyTableResponse>
  };
}

export function readSupplyTableCacheBlob(warehouseId: number): SupplyTableCacheBlob | null {
  const currentPeriod = getSupplyTableCachePeriodString();
  const mem = memoryFallback.get(warehouseId);
  if (mem) {
    return normalizeBlob(mem, warehouseId, currentPeriod);
  }
  try {
    const raw = localStorage.getItem(storageKey(warehouseId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    return normalizeBlob(parsed, warehouseId, currentPeriod);
  } catch {
    return null;
  }
}

function persistBlob(warehouseId: number, blob: SupplyTableCacheBlob): void {
  memoryFallback.set(warehouseId, blob);
  try {
    localStorage.setItem(storageKey(warehouseId), JSON.stringify(blob));
  } catch (e) {
    console.warn('[supplyTableCache] localStorage no disponible o cuota excedida; usando solo memoria de sesión.', e);
  }
}

export function invalidateSupplyTableCache(warehouseId: number): void {
  notifySupplyTableCacheInvalidated(warehouseId);
  memoryFallback.delete(warehouseId);
  try {
    localStorage.removeItem(storageKey(warehouseId));
  } catch {
    /* ignore */
  }
}

export function invalidateAllSupplyTableCaches(): void {
  notifySupplyTableCacheInvalidated();
  memoryFallback.clear();
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(`${STORAGE_PREFIX}:`)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch {
    /* ignore */
  }
}

/** Hash estable para los query params que definen una respuesta de tabla API. */
export function hashSupplyTableApiFilters(filters: Record<string, unknown>): string {
  const keys = Object.keys(filters).sort();
  return keys.map((k) => `${k}=${JSON.stringify(filters[k])}`).join('&');
}

export function tryReadMergedFromCache(
  warehouseId: number,
  _applyMoqMp?: boolean,
): SupplyTableResponse | null {
  const blob = readSupplyTableCacheBlob(warehouseId);
  if (!blob?.merged?.response) return null;
  return blob.merged.response;
}

export function writeMergedToCache(
  warehouseId: number,
  response: SupplyTableResponse,
  _applyMoqMp?: boolean,
): void {
  const period = getSupplyTableCachePeriodString();
  let prev = readSupplyTableCacheBlob(warehouseId);
  if (!prev || prev.cachePeriod !== period) {
    prev = emptyBlob(period);
  }
  const blob: SupplyTableCacheBlob = {
    ...prev,
    version: CACHE_VERSION,
    cachePeriod: period,
    merged: { response },
  };
  persistBlob(warehouseId, blob);
}

export function tryReadActionableFromCache(
  warehouseId: number,
  _applyMoqMp?: boolean,
): SupplyTableResponse | null {
  const blob = readSupplyTableCacheBlob(warehouseId);
  if (!blob?.actionable?.response) return null;
  return blob.actionable.response;
}

export function writeActionableToCache(
  warehouseId: number,
  response: SupplyTableResponse,
  _applyMoqMp?: boolean,
): void {
  const period = getSupplyTableCachePeriodString();
  let prev = readSupplyTableCacheBlob(warehouseId);
  if (!prev || prev.cachePeriod !== period) {
    prev = emptyBlob(period);
  }
  const blob: SupplyTableCacheBlob = {
    ...prev,
    version: CACHE_VERSION,
    cachePeriod: period,
    actionable: { response },
  };
  persistBlob(warehouseId, blob);
}

export function tryReadSplitBundleFromCache(
  warehouseId: number,
  suggestedGreaterZero: boolean,
  tableFiltersHash: string
): { kpi: SupplyTableResponse; table: SupplyTableResponse } | null {
  const blob = readSupplyTableCacheBlob(warehouseId);
  if (!blob?.splitKpi || !blob.splitTables[tableFiltersHash]) return null;
  if (blob.splitKpi.suggestedGreaterZero !== suggestedGreaterZero) return null;
  return {
    kpi: blob.splitKpi.response,
    table: blob.splitTables[tableFiltersHash]
  };
}

export function writeSplitKpiToCache(
  warehouseId: number,
  suggestedGreaterZero: boolean,
  response: SupplyTableResponse
): void {
  const period = getSupplyTableCachePeriodString();
  let prev = readSupplyTableCacheBlob(warehouseId);
  if (!prev || prev.cachePeriod !== period) {
    prev = emptyBlob(period);
  }
  const blob: SupplyTableCacheBlob = {
    ...prev,
    version: CACHE_VERSION,
    cachePeriod: period,
    splitKpi: { suggestedGreaterZero, response },
    splitTables: prev.splitTables
  };
  persistBlob(warehouseId, blob);
}

export function writeSplitTableToCache(
  warehouseId: number,
  tableFiltersHash: string,
  response: SupplyTableResponse
): void {
  const period = getSupplyTableCachePeriodString();
  let prev = readSupplyTableCacheBlob(warehouseId);
  if (!prev || prev.cachePeriod !== period) {
    prev = emptyBlob(period);
  }
  const blob: SupplyTableCacheBlob = {
    ...prev,
    version: CACHE_VERSION,
    cachePeriod: period,
    splitTables: { ...prev.splitTables, [tableFiltersHash]: response }
  };
  persistBlob(warehouseId, blob);
}

/** Lo emite MainLayout junto al almacén en /supply; lo escucha SupplyPage. */
export const SUPPLY_REFRESH_SUGGESTED_TABLE_EVENT = 'stockerai-supply-refresh-suggested-table';
